import { ContentType } from "@brookbush/database";
import type { CoursePreview } from "@brookbush/types";

import Image from "next/image";
import Link from "next/link";

import BookmarkButton from "~/components/BookmarkButton";
import SectionContainer from "~/components/SectionContainer";
import ShareButton from "~/components/ShareButton";

interface LandingPagePopularCoursesProps {
	courses: CoursePreview[];
}

const LandingPagePopularCourses = ({
	courses,
}: LandingPagePopularCoursesProps) => {
	if (!courses?.length) return null;

	return (
		<SectionContainer className="flex flex-col items-center px-2 py-12">
			<h2 className="text-center font-bold text-2xl sm:text-4xl lg:text-4xl">
				Popular Courses
			</h2>

			<div className="relative grid gap-16 py-10 md:px-8 xl:grid-cols-2">
				{courses.map((course) => (
					<div
						key={course._id}
						className="flex flex-col gap-4 rounded-2xl transition-all sm:flex-row sm:gap-6"
					>
						<Link
							prefetch={false}
							target="_blank"
							className="relative h-48 w-full shrink-0 overflow-hidden rounded-xl border hover:brightness-95 sm:h-44 sm:w-56"
							href={`/courses/${course.slug}`}
						>
							<Image
								src={
									course.thumbnail?.url ||
									"/logos/brookbush-institute-logo-icon.png"
								}
								alt={course.title}
								fill
								className={
									course.thumbnail
										? "object-cover"
										: "object-contain p-4 brightness-95 dark:invert dark:saturate-0"
								}
							/>
						</Link>

						<div className="flex w-full justify-between gap-2">
							<div className="group flex flex-col gap-2">
								<Link href={`/courses/${course.slug}`}>
									<h2 className="line-clamp-2 font-semibold text-xl hover:underline sm:text-2xl">
										{course.title}
									</h2>{" "}
								</Link>

								<p className="line-clamp-4">{course.description}</p>

								<div className="flex gap-2">
									<div className="flex items-center rounded-full bg-amber-100 px-3 py-1.5 text-base dark:text-slate-800">
										{course.credits} {course.credits > 1 ? "Credits" : "Credit"}
									</div>

									<BookmarkButton
										contentId={course._id}
										contentType={ContentType.COURSE}
									/>
									<ShareButton href={`/courses/${course.slug}`} />
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</SectionContainer>
	);
};

export default LandingPagePopularCourses;
